import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Quote, Sparkles, Clock, ShieldCheck, Cpu, Zap, Eye, BatteryCharging, Waves, Heart, Star, ArrowRight, Check } from "lucide-react";
import { IMG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { posterUrl } from "@/data/pdpMedia";
import { VideoReel } from "@/components/product/VideoReel";
import { Reveal, SectionHeading, Overline, Stars } from "@/components/common/ui";
import { Blush } from "@/components/common/Decor";

/* ───────── As Seen In, featured article ───────── */
export const AsSeenIn = () => (
  <section className="relative overflow-hidden bg-[#FFFCFA] py-20 md:py-28" data-testid="as-seen-in">
    <Blush className="-left-20 top-0" color="#FBC4A8" size="22rem" style={{ opacity: 0.12 }} />
    <div className="relative z-10 mx-auto max-w-[1000px] px-6 text-center md:px-12">
      <Overline className="bg-white/70">As Seen In</Overline>
      <Quote className="mx-auto mt-6 h-10 w-10 text-[#FBC4A8]" fill="#FBC4A8" />
      <p className="mx-auto mt-5 max-w-3xl font-display text-3xl font-light leading-snug text-[#2C2B2C] sm:text-4xl">
        “The first at-home LED device that feels genuinely <span className="text-[#E97C6A]">luxurious</span>, clinical results wrapped in a design you'll actually want on your shelf.”
      </p>
      <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#8A7F86]">Beauty Editor, GLOW Magazine</p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[#9A8E94]">
        {["VOGUE", "GLOW", "marie claire", "BYRDIE", "ELLE"].map((p) => (
          <span key={p} className="font-display text-2xl tracking-wide opacity-70">{p}</span>
        ))}
      </div>
    </div>
  </section>
);

/* ───────── Anatomy diagram, callouts beside the mask ───────── */
const ANATOMY = [
  { icon: Cpu, title: "114 medical-grade LEDs", desc: "Salon-level density for an even, full-face dose." },
  { icon: Waves, title: "4 clinical wavelengths", desc: "Red, blue, pink & near-infrared in one device." },
  { icon: Eye, title: "Built-in eye shields", desc: "Protects your eyes through every session." },
  { icon: Heart, title: "Flexible medical silicone", desc: "Contours to your face, no rigid plastic, no marks." },
  { icon: Zap, title: "One-touch smart remote", desc: "Switch modes & timing without lifting a finger." },
  { icon: BatteryCharging, title: "USB-C rechargeable", desc: "Cordless, travel-ready, 10-minute sessions." },
];
const Callout = ({ icon: Icon, title, desc, side }) => (
  <Reveal className={`group flex items-start gap-4 ${side === "right" ? "flex-row-reverse text-right" : ""}`}>
    <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#232222] transition-transform group-hover:scale-110"><Icon className="h-5 w-5" /></span>
    <div className={`mt-5 hidden h-px w-10 flex-none bg-[#FBC4A8] lg:block ${side === "right" ? "order-first" : ""}`} />
    <div>
      <p className="font-display text-lg text-[#2C2B2C]">{title}</p>
      <p className="mt-1 text-sm font-light leading-snug text-[#8A7F86]">{desc}</p>
    </div>
  </Reveal>
);
export const AnatomyDiagram = () => (
  <section className="relative overflow-hidden bg-[#FFFCFA] py-24 md:py-32" data-testid="anatomy-diagram">
    <div className="mx-auto max-w-[1400px] px-6 md:px-12">
      <SectionHeading align="center" overline="Engineered to the Detail"
        title={<>A closer look at your <span className="text-[#E97C6A]">glow</span> device.</>} className="mb-16" />
      <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.1fr_1fr] lg:gap-10">
        <div className="space-y-10 lg:order-1">{ANATOMY.slice(0, 3).map((a) => <Callout key={a.title} {...a} side="left" />)}</div>
        <div className="relative lg:order-2">
          <Blush className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" color="#FBC4A8" size="20rem" style={{ opacity: 0.14 }} />
          <div className="relative z-10 overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] glow-ring">
            <img src={IMG.deviceDetail} alt="Leopard Luxe LED mask anatomy" className="aspect-square w-full object-cover" />
          </div>
        </div>
        <div className="space-y-10 lg:order-3">{ANATOMY.slice(3).map((a) => <Callout key={a.title} {...a} side="right" />)}</div>
      </div>
    </div>
  </section>
);

/* ───────── Features → Benefits table ───────── */
const FB = [
  { icon: Sparkles, f: "630nm red light", b: "Rebuilds collagen so fine lines visibly soften and skin firms." },
  { icon: ShieldCheck, f: "415nm blue light", b: "Kills acne-causing bacteria to clear & prevent breakouts." },
  { icon: Waves, f: "850nm near-infrared", b: "Repairs deep tissue & strengthens your skin barrier." },
  { icon: Heart, f: "Flexible silicone fit", b: "Even light contact for faster, more uniform results." },
  { icon: Clock, f: "10-minute sessions", b: "Salon-grade skincare that fits a real morning." },
  { icon: Zap, f: "UV-free & heat-free", b: "Gentle enough for sensitive skin, with zero downtime." },
];
export const FeatureBenefitTable = () => {
  const [active, setActive] = useState(0);
  const A = FB[active];
  return (
    <section className="bg-white py-24 md:py-32" data-testid="feature-benefit-table">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <SectionHeading align="center" overline="Features → Benefits"
          title={<>Every feature earns its <span className="text-[#E97C6A]">place.</span></>}
          sub="Tap any feature to see exactly what it does for your skin." className="mb-14" />
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
          <div className="space-y-3">
            {FB.map((item, i) => {
              const Icon = item.icon; const on = i === active;
              return (
                <button key={item.f} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} data-testid={`feature-row-${i}`}
                  className={`flex w-full items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all ${on ? "border-[#FBC4A8] bg-[#FBC4A8]/25 shadow-sm" : "border-[#F2E8E6] bg-white hover:border-[#FBC4A8]"}`}>
                  <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-full transition-colors ${on ? "bg-[#FBC4A8] text-[#232222]" : "bg-[#FFFCFA] text-[#8A7F86]"}`}><Icon className="h-5 w-5" /></span>
                  <span className="font-display text-lg text-[#2C2B2C]">{item.f}</span>
                  <ArrowRight className={`ml-auto h-4 w-4 flex-none text-[#2C2B2C] transition-opacity ${on ? "opacity-100" : "opacity-0"}`} />
                </button>
              );
            })}
          </div>
          <div className="relative flex min-h-[20rem] items-center overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] bg-[#FFFCFA] p-10">
            <Blush className="-right-10 -top-10" color="#FBC4A8" size="14rem" style={{ opacity: 0.16 }} />
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }} className="relative z-10">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#232222] shadow-sm"><A.icon className="h-7 w-7" /></span>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#232222]">{A.f}</p>
                <p className="mt-3 font-display text-2xl font-light leading-snug text-[#2C2B2C] sm:text-3xl">{A.b}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────── Who it's for ───────── */
const WHO_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_leopard-luxe-glow/artifacts/drzi48lm_Aura%20Glow%20%28Instagram%20Post%20%2845%29%29.png", name: "Chloe", age: 22, concern: "Acne & breakouts", weeks: "8 weeks" },
  { src: "https://customer-assets.emergentagent.com/job_leopard-luxe-glow/artifacts/n0fpr12x_Aura%20Glow%20%28Instagram%20Post%20%2845%29%29%20%281%29.png", name: "Sophie", age: 26, concern: "Breakouts & redness", weeks: "10 weeks" },
  { src: "https://customer-assets.emergentagent.com/job_leopard-luxe-glow/artifacts/164je6bt_Aura%20Glow%20%28Instagram%20Post%20%2845%29%29%20%282%29.png", name: "Diane", age: 52, concern: "Fine lines & wrinkles", weeks: "9 weeks" },
  { src: "https://static.prod-images.emergentagent.com/jobs/6e9e37ac-1f04-43a6-819a-203cd06632b4/images/46bbd2bd8e44c08dbde49793c977bb031e0fe3bb2d5507927c8a5ebbc01455da.png", name: "Helen", age: 54, concern: "Forehead wrinkles & frown lines", weeks: "12 weeks" },
  { src: "https://static.prod-images.emergentagent.com/jobs/6e9e37ac-1f04-43a6-819a-203cd06632b4/images/f3ffeed4842a63a2c59779ba5ff256c8d9244116020837da2b887354a013d57c.png", name: "Susan", age: 56, concern: "Neck lines & firmness", weeks: "12 weeks" },
  { src: "https://customer-assets.emergentagent.com/job_leopard-luxe-glow/artifacts/wyroqkb0_Aura%20Glow%20%28Instagram%20Post%20%2845%29%29%20%283%29.png", name: "Rachel", age: 39, concern: "Redness & dryness", weeks: "10 weeks" },
];

const WhoCarousel = () => {
  const [idx, setIdx] = useState(0);
  const p = WHO_PHOTOS[idx];
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % WHO_PHOTOS.length), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <Reveal delay={0.1} className="order-first lg:order-none">
      <div className="relative overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] shadow-[0_45px_100px_-44px_rgba(120,80,55,0.45)]">
        <div className="relative aspect-square w-full bg-[#F5EFE3]">
          {WHO_PHOTOS.map((ph, i) => (
            <img key={i} src={ph.src} alt={`${ph.name}'s results with the Leopard Luxe mask`} loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out"
              style={{ opacity: i === idx ? 1 : 0 }} />
          ))}

          {/* Before & After tag */}
          <div className="absolute left-4 top-4 rounded-full bg-[#F2E8E6]/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#232222] shadow-sm backdrop-blur-sm">
            Before &amp; After
          </div>

          {/* Identity badge */}
          <div className="absolute inset-x-3 bottom-12 sm:inset-x-4">
            <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex items-center justify-between gap-3 rounded-2xl bg-white/90 px-4 py-3 shadow-[0_10px_30px_-12px_rgba(120,80,55,0.5)] backdrop-blur-md"
              data-testid="who-identity-badge">
              <div className="min-w-0">
                <p className="truncate font-display text-base text-[#2C2B2C]">{p.name}<span className="font-light text-[#6E6168]">, {p.age}</span></p>
                <p className="truncate text-[11px] font-light text-[#6E6168]">{p.concern}</p>
              </div>
              <span className="flex-none rounded-full bg-[#F2E8E6] px-3 py-1 text-[11px] font-semibold text-[#232222]">{p.weeks}</span>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
          {WHO_PHOTOS.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`who-dot-${i}`} aria-label={`Show result ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`} />
          ))}
        </div>
      </div>
    </Reveal>
  );
};

const PERSONAS = [
  "Softer-looking fine lines & wrinkles",
  "Brighter, more even skin tone",
  "Calmer-looking skin with less redness",
  "Clearer skin with fewer blemishes",
  "Healthier skin that glows",
];
export const WhoItsFor = () => (
  <section className="bg-[#FFFCFA] py-24 md:py-32" data-testid="who-its-for">
    <div className="mx-auto max-w-[1300px] px-6 md:px-12">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Left — heading + benefits */}
        <div>
          <SectionHeading overline="Made For You"
            title={<>Healthy, glowing skin starts with just <span className="text-[#E97C6A]">10 minutes a day.</span></>}
            sub="Experience professional-grade LED technology designed to target multiple skin concerns with clinically backed wavelengths, all from the comfort of your home." className="mb-9" />
          <ul className="space-y-4">
            {PERSONAS.map((t, i) => (
              <Reveal key={t} delay={i * 0.06} className="group flex items-center gap-4">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#FFFFFF] transition-transform group-hover:scale-110">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <h3 className="font-display text-lg text-[#2C2B2C] md:text-xl">{t}</h3>
              </Reveal>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="who-its-for-links">
            {[["The Science", "light-spectrum"], ["Read Reviews", "testimonials-section"]].map(([label, tid]) => (
              <button key={tid} onClick={() => document.querySelector(`[data-testid="${tid}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                data-testid={`who-link-${tid}`}
                className="group inline-flex items-center gap-1.5 text-sm font-medium text-[#2C2B2C] transition-colors hover:text-[#C8765A]">
                <span className="border-b border-[#FBC4A8] pb-0.5">{label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-[#C8765A] transition-transform group-hover:translate-x-1" />
              </button>
            ))}
            <Link to="/led-light-therapy" data-testid="who-link-article"
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-[#2C2B2C] transition-colors hover:text-[#C8765A]">
              <span className="border-b border-[#FBC4A8] pb-0.5">About LED Light Therapy</span>
              <ArrowRight className="h-3.5 w-3.5 text-[#C8765A] transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-7 max-w-md text-sm font-light leading-relaxed text-[#8A7F86]" data-testid="who-its-for-disclaimer">
            Leopard Luxe™ is designed to help address a wide range of common skin concerns. With consistent use, many customers notice visible improvements in skin tone, texture and overall radiance over 8 to 12 weeks. Individual results may vary.
          </p>
        </div>
        {/* Right — single photo carousel */}
        <WhoCarousel />
      </div>
    </div>
  </section>
);

/* ───────── UGC video wall ───────── */
const VIDEOS = [
  { vid: "pdp_ugc_1", poster: IMG.lifestyleMask, name: "@sophie.glow", cap: "My 8-week transformation" },
  { vid: "pdp_ugc_2", poster: IMG.beforeAfter, name: "@mia.skin", cap: "Unboxing the leopard luxe" },
  { vid: "pdp_ugc_3", poster: IMG.resultB, name: "@theglowedit", cap: "10-min nightly ritual" },
  { vid: "pdp_ugc_4", poster: IMG.glowFront, name: "@beautybyana", cap: "Real results, no filter" },
];
const UGCTile = ({ v, i, onOpen }) => (
  <Reveal delay={i * 0.06} className="group relative overflow-hidden rounded-[1.6rem] border border-white/10">
    <div className="relative aspect-[9/16] w-full bg-black/30">
      <video
        src={M(v.vid)}
        poster={posterUrl(v.vid)}
        data-media-slot={v.vid}
        data-media-type="video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        data-testid={`ugc-video-${i}`}
        className="h-full w-full object-cover"
      />
      <button onClick={() => onOpen(i)} data-testid={`ugc-play-${i}`} className="absolute inset-0">
        <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#FBC4A8] text-[#2C2B2C] transition-transform group-hover:scale-110"><Play className="h-5 w-5 fill-[#2C2B2C]" /></span>
        <span className="absolute bottom-3 left-3 right-3 text-left">
          <span className="block text-xs font-semibold text-white">{v.name}</span>
          <span className="block text-[0.68rem] text-white/80">{v.cap}</span>
        </span>
      </button>
    </div>
  </Reveal>
);

export const UGCVideos = () => {
  const [reelIndex, setReelIndex] = useState(null);
  const reelClips = VIDEOS.map((v) => ({ src: M(v.vid), poster: posterUrl(v.vid), title: v.name, sub: v.cap }));
  return (
    <section className="relative overflow-hidden bg-[#232222] py-24 text-[#FFFCFA] md:py-32" data-testid="ugc-videos">
      <Blush className="right-0 top-1/4" color="#FBC4A8" size="28rem" style={{ opacity: 0.16 }} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHeading align="center" overline="Glow-Ups On Camera"
          title={<span className="text-[#FFFCFA]">Real people, real <span className="text-[#FBC4A8]">results.</span></span>}
          sub={<span className="text-[#F2E8E6]">Tap to watch the Leopard Luxe™ in action.</span>}
          className="mb-14 [&_h2]:text-[#FFFCFA]" />
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:grid md:grid-cols-4 md:gap-6 md:overflow-visible md:pb-0 [&::-webkit-scrollbar]:hidden">
          {VIDEOS.map((v, i) => (
            <div key={i} className="w-[58%] shrink-0 snap-start sm:w-[42%] md:w-auto">
              <UGCTile v={v} i={i} onOpen={setReelIndex} />
            </div>
          ))}
        </div>
      </div>
      {reelIndex !== null && (
        <VideoReel clips={reelClips} startIndex={reelIndex} onClose={() => setReelIndex(null)} />
      )}
    </section>
  );
};
